import { useDispatch, useSelector } from "react-redux";
import "./App.css";
import React, { useEffect, useState } from "react";
import PageRoutes from "./routes/PageRoutes";
import MainLayout from "./modules/layout/components/MainLayout";
import { RootState } from "./redux/store";
import Notifier from "./modules/common/Notifier";
import { useGetHintListQuery } from "./api/hints/hintsApiSlice";
import { setHints } from "./redux/slice/hints/hintsSlice";
import { useFetchAllGroups } from "./redux/flows/fetchAllGroupsFlow";
import { useFetchAllDepots } from "./redux/flows/fetchAllDepotsFlow";
import { useLoadConsent } from "./redux/flows/loadConsentFlow";
import { setLocations } from "./redux/slice/booking/bookingSlice";
import { useGetLocationListQuery } from "./api/locations/locationsApiSlice";
import { useMatch } from "react-router";
import routes from "./routes/routes";
import {getTenantName} from "./util";
import { isSharedMode, applyRuntimeCssVariables } from "./util/tenantConfig";
import { loadTenantConfig } from "./redux/slice/config/configSlice";
import { AppDispatch } from "./redux/store";
import AuthentificationProvider from "authentication/AuthentificationProvider";
import useAccountSessionInit from "modules/account/hooks/useAccountSessionInit";

function App({ instance }: { instance?: any }) {
  const dispatch = useDispatch<AppDispatch>();
  const stateLocations = useSelector((state: RootState) => state.booking.locations);
  const [fetchedGroups, setFetchedGroups] = useState(false);
  const [fetchedDepots, setFetchedDepots] = useState(false);
  const { data: hints } = useGetHintListQuery(null);
  const fetchAllGroups = useFetchAllGroups();
  const fetchAllDepots = useFetchAllDepots();
  const loadConsent = useLoadConsent();
  const consent = useSelector((state: RootState) => state.consent.consent);
  const { data: locations } = useGetLocationListQuery(null);
  const showToolBar = useSelector((state: RootState) => state.consent.consent !== null);
  // H-3a account session restore — no-op unless the tenant has accountsEnabled.
  useAccountSessionInit();

    const locationP2PRouteMatch = useMatch("/recipient/orders");
    const senderRetrievalP2PRouteMatch = useMatch("/finishRental/:token");
    const keyHandoverLocationSelectionMatch = useMatch(routes.keyHandoverLocationSelectionPath);
    const keyHandoverPaymentModelMatch = useMatch(routes.keyHandoverPaymentModelPath);
    const keyHandoverSubscriptionChoiceMatch = useMatch(routes.keyHandoverSubscriptionChoicePath);
    const keyHandoverSubscriptionPlanSelectionMatch = useMatch(
        routes.keyHandoverSubscriptionPlanSelectionPath
    );
    const keyHandoverSubscriptionEmailInputMatch = useMatch(
        routes.keyHandoverSubscriptionEmailInputPath
    );
    const keyHandoverSubscriptionVerifyCodeMatch = useMatch(
        routes.keyHandoverSubscriptionVerifyCodePath
    );
    const keyHandoverSubscriptionHostDataMatch = useMatch(
        routes.keyHandoverSubscriptionHostDataPath
    );
    const keyHandoverSubscriptionSummaryMatch = useMatch(routes.keyHandoverSubscriptionSummaryPath);
    const keyHandoverSubscriptionPaymentMatch = useMatch(routes.keyHandoverSubscriptionPaymentPath);
    const keyHandoverSubscriptionSuccessMatch = useMatch(routes.keyHandoverSubscriptionSuccessPath);
    const keyHandoverSubscriptionLoginMatch = useMatch(routes.keyHandoverSubscriptionLoginPath);
    const keyHandoverSubscriptionVerifyMatch = useMatch(routes.keyHandoverSubscriptionVerifyPath);
    const keyHandoverServiceSelectionMatch = useMatch(routes.keyHandoverServiceSelectionPath);
    const keyHandoverScheduleMatch = useMatch(routes.keyHandoverSchedulePath);
    const keyHandoverReturnScheduleMatch = useMatch(routes.keyHandoverReturnSchedulePath);
    const keyHandoverHostDetailsMatch = useMatch(routes.keyHandoverHostDetailsPath);
    const keyHandoverGuestDetailsMatch = useMatch(routes.keyHandoverGuestDetailsPath);
    const keyHandoverSummaryMatch = useMatch(routes.keyHandoverSummaryPath);
    const roomWeekMatch = useMatch(routes.roomWeekPath);
    // #3075: embedded in a website — no app toolbar, the wide layout.
    const publicPlanMatch = useMatch(routes.publicPlanPath);
    const showToolBarBasedOnRoute =
        !(
            locationP2PRouteMatch ||
            senderRetrievalP2PRouteMatch ||
            keyHandoverLocationSelectionMatch ||
            keyHandoverPaymentModelMatch ||
            keyHandoverSubscriptionChoiceMatch ||
            keyHandoverSubscriptionPlanSelectionMatch ||
            keyHandoverSubscriptionEmailInputMatch ||
            keyHandoverSubscriptionVerifyCodeMatch ||
            keyHandoverSubscriptionHostDataMatch ||
            keyHandoverSubscriptionSummaryMatch ||
            keyHandoverSubscriptionPaymentMatch ||
            keyHandoverSubscriptionSuccessMatch ||
            keyHandoverSubscriptionLoginMatch ||
            keyHandoverSubscriptionVerifyMatch ||
            keyHandoverServiceSelectionMatch ||
            keyHandoverScheduleMatch ||
            keyHandoverReturnScheduleMatch ||
            keyHandoverHostDetailsMatch ||
            keyHandoverGuestDetailsMatch ||
            keyHandoverSummaryMatch ||
            publicPlanMatch
        ) && showToolBar;

    const loadGoogleMapsScript = (apiKey: string) => {
        if (!window.google && !document.querySelector('script[src*="maps.googleapis.com"]')) {
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);

            script.onload = () => {
            };
        }
    };

    useEffect(() => {
        if (locations) {
            dispatch(setLocations(locations));
        }
    }, [dispatch, locations]);

    useEffect(() => {
        const getAssetGroups = async () => {
            await fetchAllGroups();
        };
        if (!fetchedGroups && stateLocations.length > 0) {
            getAssetGroups();
            setFetchedGroups(true);
        }
    }, [fetchAllGroups, stateLocations, fetchedGroups]);

    useEffect(() => {
        const getDepots = async () => {
            await fetchAllDepots();
        };
        if (!fetchedDepots && stateLocations.length > 0) {
            getDepots();
            setFetchedDepots(true);
        }
    }, [fetchAllDepots, stateLocations, fetchedDepots]);

    useEffect(() => {
        if (hints && hints.data) {
            dispatch(setHints(hints.data));
        }
    }, [hints, dispatch]);

    useEffect(() => {
        loadConsent();
    }, [loadConsent]);

    useEffect(() => {
        if (consent) {
            loadGoogleMapsScript(process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "");
        }
    }, [consent]);

    useEffect(() => {
        // Shared deployment: branding colors come from the runtime config, set as
        // CSS variables. Legacy/dev builds import the tenant's bundled variables.css.
        if (isSharedMode()) {
            applyRuntimeCssVariables();
            return;
        }
        const tenantName = getTenantName();
        if (tenantName) {
            import(`./tenants/${tenantName}/variables.css`).catch((error) => {
                console.error(
                    "Error while importing tenant variables.css. Keeping default variables",
                    error
                );
            });
        }
    }, []);

    // Tenant config drives service-gating decisions in PageRoutes; load it at
    // app init so direct-paste URLs (which don't traverse the home/Toolbar
    // codepath) still see the correct services list before any gate fires.
    useEffect(() => {
        dispatch(loadTenantConfig());
    }, [dispatch]);

    return (
        <MainLayout showToolBar={showToolBarBasedOnRoute} wide={!!roomWeekMatch || !!publicPlanMatch}>
            <Notifier />
            <AuthentificationProvider instance={instance}>
                <PageRoutes />
            </AuthentificationProvider>
        </MainLayout>
    );
}

export default App;
